'use client';

import { createContext, forwardRef, useCallback, useContext } from 'react';
import type { MouseEvent } from 'react';

import type {
  HeaderLinkAction,
  HeaderLinkComponent,
  HeaderLinkProps,
  HeaderPlacement,
} from './model';

const DocumentLink: HeaderLinkComponent = forwardRef<HTMLAnchorElement, HeaderLinkProps>(
  function DocumentLink({ children, ...props }, ref) {
    return (
      <a {...props} ref={ref}>
        {children}
      </a>
    );
  },
);

// Takes the app's link component as a prop, so the rendered element type is the caller's
// stable component rather than a value read during render.
const AppLink = forwardRef<HTMLAnchorElement, HeaderLinkProps & { component: HeaderLinkComponent }>(
  function AppLink({ component: Link, ...props }, ref) {
    return <Link {...props} ref={ref} />;
  },
);

const LinkComponentContext = createContext<HeaderLinkComponent>(DocumentLink);

export const HeaderLinkProvider = LinkComponentContext.Provider;

type HeaderAnchorProps = Omit<HeaderLinkProps, 'href' | 'target' | 'rel'> & {
  action: HeaderLinkAction;
  placement: HeaderPlacement;
};

/** Renders a link action and reports the selection before the browser or router navigates. */
export const HeaderAnchor = forwardRef<HTMLAnchorElement, HeaderAnchorProps>(function HeaderAnchor(
  { action, placement, onClick, ...props },
  ref,
) {
  const appLinkComponent = useContext(LinkComponentContext);
  const handleClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      action.onSelect?.(placement);
      onClick?.(event);
    },
    [action, onClick, placement],
  );
  const href = typeof action.href === 'function' ? action.href(placement) : action.href;

  return action.navigation === 'app' ? (
    <AppLink {...props} ref={ref} component={appLinkComponent} href={href} onClick={handleClick} />
  ) : (
    <DocumentLink {...props} ref={ref} href={href} onClick={handleClick} />
  );
});
